// @flow
import React from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import { ThemeProvider } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import themeCreator from "./common/themeCreator";
import {
  preloadAnalytics,
  preloadAuth,
  preloadFirestore,
  preloadFunctions,
  preloadPerformance,
  useFirebaseApp,
} from "reactfire";
import MainRouter from "./router/MainRouter";
import { SnackbarProvider } from "notistack";
import { CookiesProvider } from "react-cookie";
import dev from "./common/dev";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import UpdatePWA from "./components/UpdatePWA";

// Our components will lazy load the
// SDKs to decrease their bundle size.
// Since we know that, we can start
// fetching them now
const preloadSDKs = (firebaseApp) => {
  return Promise.all([
    preloadFirestore({
      firebaseApp,
      async setup(firestore) {
        const initalizedStore = firestore();
        if (dev.isDevMode() && dev.useEmulators) {
          await initalizedStore.settings({
            host: "localhost:8080",
            ssl: false,
          });
        } else {
          await initalizedStore.enablePersistence({
            experimentalForceOwningTab: true,
          });
        }
        return initalizedStore;
      },
    }),
    preloadAuth({ firebaseApp }),
    preloadAnalytics({
      firebaseApp,
      async setup(analytics) {
        const initializedAnalytics = analytics();
        if (dev.isDevMode()) {
          await initializedAnalytics.setAnalyticsCollectionEnabled(false);
        }
        return initializedAnalytics;
      },
    }),
    preloadFunctions({
      firebaseApp,
      async setup(functions) {
        const initializedFunctions = functions();
        if (dev.isDevMode() && dev.useEmulators) {
          await initializedFunctions.useFunctionsEmulator(
            "http://localhost:5001"
          );
        }
        return initializedFunctions;
      },
    }),
    preloadPerformance({
      firebaseApp,
      async setup(performance) {
        const initializedPerformance = performance();
        if (dev.isDevMode()) {
          await initializedPerformance.instrumentationEnabled(false);
          await initializedPerformance.dataCollectionEnabled(false);
        }
        return initializedPerformance;
      },
    }),
  ]);
};

function App() {
  // https://reactjs.org/docs/concurrent-mode-suspense.html#approach-3-render-as-you-fetch-using-suspense
  const firebaseApp = useFirebaseApp();
  preloadSDKs(firebaseApp).then(() => Promise.resolve()); // could preload docs

  // create custom theme
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const theme = React.useMemo(() => themeCreator(prefersDarkMode), [
    prefersDarkMode,
  ]);

  // add action to all snackbars
  const notistackRef = React.createRef();
  const onClickDismiss = (key) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  return (
    <CookiesProvider>
      <ThemeProvider theme={theme}>
        <SnackbarProvider
          maxSnack={2}
          dense
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          ref={notistackRef}
          action={(key) => (
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={onClickDismiss(key)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        >
          <CssBaseline />
          <MainRouter />
        </SnackbarProvider>
        <UpdatePWA />
      </ThemeProvider>
    </CookiesProvider>
  );
}

export default App;
